<template>
  <component
    :is="to ? nuxtLink : 'div'"
    :to="to"
    data-test-id="product-card"
    :class="[
      $style['product-list-item'],
      { [$style['product-list-item--compact']]: compact },
      { [$style['product-list-item--vertical']]: vertical },
      { [$style['product-list-item--border']]: border }
    ]">
    <div :class="$style['product-list-item__image']">
      <img :src="imageUrl">
    </div>

    <div :class="$style['product-list-item__content']">
      <div v-if="product.tags && product.tags.includes('hiddengoodie')">
        <b :class="$style['product__title']">{{ product.title }}</b>
      </div>
      <div
        v-else
        :class="$style['product-list-item__container']">
        <div :class="$style['product__title']">
          <div v-if="compact && item.quantity > 1">
            {{ item.quantity }} x
          </div>
          <TextHighlight :text="product.title" :input-string="highlightText" />
        </div>
        <ProductPrice
          v-if="compact || vertical"
          :class="$style['product__price']"
          :price="product.price"
          :sale="product.originalPrice" />
        <div v-if="product.avaliableSizes.length">
          <product-size-stack :sizes="product.sizes || product.avaliableSizes" />
        </div>
        <div v-if="product.alternativeColors.length">
          <product-color-stack :colors="product.alternativeColors" />
        </div>
        <div class="h-margin-top">
          <LazyTag
            v-for="label in product.labels"
            :key="label.type"
            :class="$style['tag']"
            big
            :type="label.type">
            {{ label.title }}
          </LazyTag>
        </div>
      </div>
      <div
        v-if="product.errorMsg"
        class="h-font-size-s">
        {{ product.errorMsg }} (max. {{ product.quantity }})
      </div>

      <slot name="top-right" />

      <div
        v-if="product.colorName"
        class="cell--justify-end"
        size="6">
        <slot name="bottom-left" />
      </div>
      <ProductPrice
        v-if="!compact && !vertical"
        vertical
        :sale="
          product.originalPrice
            ? (
              parseFloat(product.originalPrice.replace(',', '.')) *
              item.quantity
            )
              .toFixed(2)
              .replace('.', ',')
            : false
        "
        :price="
          (parseFloat(product.price.replace(',', '.')) * item.quantity)
            .toFixed(2)
            .replace('.', ',')
        " />
    </div>
  </component>
</template>
<script setup>
const nuxtLink = resolveComponent('nuxt-link')
const config = useRuntimeConfig()
const props = defineProps({
  item: {
    type: Object,
    default: null
  },
  to: {
    type: Object,
    default: null
  },
  // For use in searchResult
  highlightText: {
    type: String,
    default: null
  },
  compact: { type: Boolean, default: false },
  vertical: { type: Boolean, default: false },
  border: { type: Boolean, default: false }
})

const imageUrl = computed(() => {
  if (!props.item.image && !props.item.product.images) { return false }
  return `${config.public.cdnURL}pictures/thumbs/${
            props.item.image ? props.item.image : props.item.product.images[0]
          }-thumbnail.webp`
})

const product = computed(() => {
  return props.item.image ? props.item : props.item.product
})

</script>
<style lang="scss" module>
.product-list-item {
  background-color: $white;
  color: $black;
  display: flex;
  flex-direction: row;
  overflow: hidden;
  position: relative;
  white-space: normal;
  text-decoration: none;

  &--border {
    border: 1px solid $grey-lighter;
  }

  &--compact {
    border: 0;
    border-bottom: 1px solid $grey-lighter;
    font-size: $font-size-s;
    align-items: flex-start;
  }

  &__container {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: $spacing * 0.25;

    @media (max-width: #{$breakpoint-mobile}) {
      gap: 1px;
    }

    .product__title {
      margin-bottom: $spacing * 0.75;
    }
  }

  @media (hover: hover) {
    &:hover {
      background-color: $grey-lighter;
    }
  }

  &--vertical {
    border: 1px solid $grey-lightest;

    .price {
      font-size: $font-size-s + 0.1rem;
    }

    @media (max-width: #{$breakpoint-mobile}) {
      .product-list-item__content {
        div {
          line-break: auto;
          white-space: normal;
          justify-content: flex-start;
        }
      }

      .product-list-item__image {
        height: $spacing * 24;
      }

      .product__title {
        font-size: $font-size-s;
        line-height: 125%;
      }

      .price {
        font-size: $font-size-s + 0.25rem;
      }
    }

    @media (min-width: #{$breakpoint-desktop}) {
      .product-list-item__image {
        width: $spacing * 15;
      }

      .price {
        font-size: $font-size-xs + 0.1rem;
      }
    }
  }

  &__message {
    background: rgba($white, 0.95);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    position: absolute;
    inset: 0;
    height: auto;
    width: auto;
  }

  &__image {
    display: flex;
    padding: $spacing;
    width: auto;
    max-width: $spacing * 20;

    .cart-item--compact & {
      padding: $spacing * 2;
      width: $spacing * 12;
      height: auto;
    }

    img {
      object-fit: cover;
      width: 100%;
      height: 100%;
    }
  }

  &__content {
    padding: $spacing * 2;
    padding-left: $spacing;
    flex: 1;

    .grid {
      height: 100%;
    }

    h4 {
      text-transform: none;
    }

    a {
      text-decoration: none;
      font-weight: $font-weight-bold;
    }

    .cart-item--compact & {
      padding: 0;
      padding-right: $spacing * 2;
      padding-top: $spacing * 2;
    }
  }

  @media screen and (prefers-color-scheme: darker) {
    background-color: $grey-darkest;
  }
}
</style>
